import { DatePipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, input } from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { TooltipModule } from "primeng/tooltip";

import { QuoteStatus, VersionStatusChangeLog } from "src/api-service";
import { CoreCompanyDatePipe } from "src/app/shared/pipe/core-company-date.pipe";
import {
    DETOUR_KINDS,
    DetourEpisode,
    DetourKindConfig,
    DRAWING_DETOUR_PATH,
    DRAWING_DETOUR_STATUSES,
    EpisodeFocus,
    MAIN_PATH,
    NodeState,
    RenderedNode,
    REVIEW_DETOUR_PATH,
    REVIEW_DETOUR_STATUSES,
    TERMINAL_STATUSES,
    TERMINATOR_LABELS,
} from "./quote-status-timeline.config";

/**
 * Quote status timeline — two faces:
 *
 *   compact   → a single-line status pill for tight chrome (nav header).
 *               Shows the current status name and, when applicable, a
 *               detour indicator (drawing or review). Designed to occupy
 *               roughly the same footprint as a regular tag.
 *
 *   expanded  → a full horizontal stepper for the Drawing tab. Three equal
 *               columns for the main path (Draft → Sent → Approved), with
 *               optional detour lanes above (review between Sent/Approved,
 *               drawing wherever it was sourced) connected to their source
 *               columns by L-shaped corner pieces. No SVG, no Bezier math —
 *               just CSS Grid and pseudo-element connectors.
 */
@Component({
    selector: "app-quote-status-timeline",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule, TooltipModule, CoreCompanyDatePipe, DatePipe],
    templateUrl: "./quote-status-timeline.component.html",
    styleUrl: "./quote-status-timeline.component.scss",
})
export class QuoteStatusTimelineComponent {

    public readonly status              = input.required<QuoteStatus>();
    public readonly preDrawingStatus    = input<QuoteStatus | null>(null);
    public readonly mode                = input<"compact" | "expanded">("compact");
    public readonly isSubscription      = input(false);
    public readonly statusLog           = input<VersionStatusChangeLog[]>([]);
    public readonly drawerName          = input<string | null>(null);
    public readonly drawerEmail         = input<string | null>(null);
    public readonly estimatedCompletion = input<Date | null>(null);

    // =========================================================================
    // STATE DERIVATIONS
    // =========================================================================

    public readonly isTerminal         = computed(() => TERMINAL_STATUSES.has(this.status()));
    public readonly isInDrawingDetour  = computed(() => DRAWING_DETOUR_STATUSES.has(this.status()));
    public readonly isInReviewDetour   = computed(() => REVIEW_DETOUR_STATUSES.has(this.status()));
    public readonly isInAnyDetour      = computed(() => this.isInDrawingDetour() || this.isInReviewDetour());

    /** Drawing-detour episodes. May contain one "active" episode if the quote
     *  is currently on the drawing detour. Hidden for non-subscription quotes. */
    public readonly drawingEpisodes = computed<DetourEpisode[]>(() => {
        // Hide drawing detour for regular (non-subscription) quotes
        if (!this.isSubscription()) return [];

        return this.computeEpisodes(
            DETOUR_KINDS.find(k => k.kind === "drawing")!,
            this.isInDrawingDetour(),
            this.preDrawingStatus(),
        );
    });

    /** Review-detour episodes. Same shape as drawing — anchored at whichever
     *  main status preceded ReadyForReview (typically Sent). */
    public readonly reviewEpisodes = computed<DetourEpisode[]>(() =>
        this.computeEpisodes(
            DETOUR_KINDS.find(k => k.kind === "review")!,
            this.isInReviewDetour(),
            null,
        ),
    );

    /** All detour episodes the quote has been through (or could still take),
     *  ordered for rendering. Each episode is assigned a `row`: when two
     *  episodes share a source column, the review sits closer to the main
     *  path (row 0) and the drawing floats above it (row 1), so the arches
     *  don't visually overlap. Drawing episodes are appended after review so
     *  the row-assignment loop sees review first. */
    public readonly allEpisodes = computed<DetourEpisode[]>(() => {
        const list = [...this.reviewEpisodes(), ...this.drawingEpisodes()];
        const rowByCol = new Map<number, number>();
        return list.map((ep): DetourEpisode => {
            const used = rowByCol.get(ep.sourceIdx) ?? 0;
            rowByCol.set(ep.sourceIdx, used + 1);
            return { ...ep, row: used };
        });
    });

    public readonly hasAnyDetour    = computed(() => this.allEpisodes().length > 0);
    public readonly maxEpisodeRow   = computed(() =>
        this.allEpisodes().reduce((m, ep) => Math.max(m, ep.row), 0),
    );

    /** The source of the currently active detour (drawing or review), if any.
     *  Used by the compact pill prefix ("Sent → Drawing in progress"). */
    public readonly activeDetourSource = computed<QuoteStatus | null>(() => {
        const active = this.allEpisodes().find(e => e.state === "active");
        return active?.sourceStatus ?? null;
    });

    public readonly terminator = computed<{ labelKey: string } | null>(() => {
        if (!this.isTerminal()) return null;
        return { labelKey: TERMINATOR_LABELS[this.status() as number] };
    });

    // =========================================================================
    // MAIN PATH
    // =========================================================================

    public readonly mainNodes = computed<RenderedNode[]>(() => {
        const status   = this.status();
        const activeSrc = this.activeDetourSource();
        const inDetour = this.isInAnyDetour();
        const terminal = this.isTerminal();

        const anchorIdx = (() => {
            if (terminal) return -1;
            if (inDetour && activeSrc != null) {
                return MAIN_PATH.findIndex(n => n.status === activeSrc);
            }
            return MAIN_PATH.findIndex(n => n.status === status);
        })();

        return MAIN_PATH.map((node, i): RenderedNode => {
            let state: NodeState;
            if (terminal) {
                const seen = this.statusLog().some(e => e.toStatus === node.status);
                state = seen ? "completed" : "future";
            } else if (anchorIdx === -1) {
                state = "future";
            } else if (i < anchorIdx) {
                state = "completed";
            } else if (i === anchorIdx) {
                state = "current";
            } else {
                state = "future";
            }
            return { ...node, state, date: this.dateForStatus(node.status) };
        });
    });

    // =========================================================================
    // COMPACT PILL CONTENT
    // =========================================================================

    /** The status label shown in the compact pill. */
    public readonly currentLabelKey = computed(() => {
        const status = this.status();
        const node = MAIN_PATH.find(n => n.status === status)
                  ?? DRAWING_DETOUR_PATH.find(n => n.status === status)
                  ?? REVIEW_DETOUR_PATH.find(n => n.status === status);
        if (node) return node.labelKey;
        if (this.isTerminal()) return TERMINATOR_LABELS[status as number];
        return "QUOTE_LIST.STATUS_LABEL.DRAFT";
    });

    public readonly currentIcon = computed(() => {
        const status = this.status();
        const node = MAIN_PATH.find(n => n.status === status)
                  ?? DRAWING_DETOUR_PATH.find(n => n.status === status)
                  ?? REVIEW_DETOUR_PATH.find(n => n.status === status);
        if (node) return node.icon;
        if (this.isTerminal()) return "pi-times";
        return "pi-circle";
    });

    /** When in any detour, also surface the source state for context
     *  ("Sent → Drawing", "Sent → Under review"). */
    public readonly compactPrefixLabelKey = computed(() => {
        if (!this.isInAnyDetour()) return null;
        const src = this.activeDetourSource();
        if (src == null) return null;
        return MAIN_PATH.find(n => n.status === src)?.labelKey ?? null;
    });

    // =========================================================================
    // ACCESSIBILITY
    // =========================================================================

    public readonly a11ySummary = computed(() => {
        if (this.isTerminal()) {
            return `Quote terminated: ${QuoteStatus[this.status()]}.`;
        }
        if (this.isInDrawingDetour()) {
            const src = this.activeDetourSource();
            const others = this.drawingEpisodes().filter(e => e.state === "completed").length;
            const otherText = others > 0 ? ` (plus ${others} previous drawing detour${others === 1 ? "" : "s"})` : "";
            return `Currently in drawing detour. Returns to ${src != null ? QuoteStatus[src] : "previous status"} after drawing completes${otherText}.`;
        }
        if (this.isInReviewDetour()) {
            const src = this.activeDetourSource();
            const others = this.reviewEpisodes().filter(e => e.state === "completed").length;
            const otherText = others > 0 ? ` (plus ${others} previous review${others === 1 ? "" : "s"})` : "";
            return `Currently under review. Returns to ${src != null ? QuoteStatus[src] : "previous status"} after review completes${otherText}.`;
        }
        return `Status: ${QuoteStatus[this.status()]}.`;
    });

    // =========================================================================
    // HELPERS
    // =========================================================================

    /** Inline grid-cols var for the stepper. Each arch carries its own
     *  source-col, and `--qst-max-row` drives padding-top so stacked arches
     *  get enough vertical room above the main path.
     *
     *  `--qst-cols` stays at the main-path length so detour arches keep
     *  their column positions stable. `--qst-cols-total` drives the main
     *  grid layout and grows by one when the quote is in a terminal status
     *  so the terminator (Lost / Archived / …) gets its own column. */
    public readonly cssVars = computed(() => ({
        "--qst-cols":       String(MAIN_PATH.length),
        "--qst-cols-total": String(MAIN_PATH.length + (this.isTerminal() ? 1 : 0)),
        "--qst-max-row":    String(this.maxEpisodeRow()),
    }));

    /** 1-based source column for a given episode (template-friendly). */
    public arxSourceCol(ep: DetourEpisode): string {
        return String(ep.sourceIdx + 1);
    }

    private dateForStatus(s: QuoteStatus): Date | null {
        const entry = [...this.statusLog()].reverse().find(e => e.toStatus === s);
        return entry ? this.toLocalDate(entry.created) : null;
    }

    /** Normalise a log timestamp into a real `Date` object.
     *
     *  Azure App Service serialises UTC `DateTime`s as ISO strings that
     *  sometimes arrive without a `Z` suffix or offset. `new Date(string)`
     *  parses those naive strings as *local* time, which would display the
     *  UTC clock value as if it were local — silently wrong. We force a `Z`
     *  when no timezone marker is present, so the value is parsed as UTC
     *  and Angular's `DatePipe` then converts it to the user's local zone
     *  on render. Values that are already `Date` objects or already carry
     *  timezone info are passed through unchanged. */
    private toLocalDate(value: Date | string): Date {
        if (value instanceof Date) return value;
        const s = String(value);
        return new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`);
    }

    /** Walk the status log and reconstruct every detour episode of a given
     *  kind. Shared between drawing and review — the kind config supplies
     *  which statuses count as "inside" the detour and which sub-status is
     *  the entry vs progress node. */
    private computeEpisodes(
        cfg: DetourKindConfig,
        isCurrentlyInside: boolean,
        fallbackSource: QuoteStatus | null,
    ): DetourEpisode[] {
        const log = this.statusLog();
        const episodes: DetourEpisode[] = [];

        type Working = {
            sourceStatus: QuoteStatus;
            sourceIdx: number;
            entryDate: Date | null;
            progressDate: Date | null;
            wasInProgress: boolean;
        };
        let current: Working | null = null;

        for (const e of log) {
            const fromIsDetour = cfg.statuses.has(e.fromStatus);
            const toIsDetour   = cfg.statuses.has(e.toStatus);

            if (!fromIsDetour && toIsDetour) {
                // Episode starts (transition from main → this detour).
                const srcIdx = MAIN_PATH.findIndex(n => n.status === e.fromStatus);
                if (srcIdx >= 0) {
                    const created = this.toLocalDate(e.created);
                    current = {
                        sourceStatus: e.fromStatus,
                        sourceIdx: srcIdx,
                        entryDate:    e.toStatus === cfg.entryStatus    ? created : null,
                        progressDate: e.toStatus === cfg.progressStatus ? created : null,
                        wasInProgress: e.toStatus === cfg.progressStatus,
                    };
                }
            } else if (fromIsDetour && toIsDetour && current) {
                // Sub-status transition within the current episode.
                const created = this.toLocalDate(e.created);
                if (e.toStatus === cfg.entryStatus)    current.entryDate = created;
                if (e.toStatus === cfg.progressStatus) { current.progressDate = created; current.wasInProgress = true; }
            } else if (fromIsDetour && !toIsDetour && current) {
                // Episode ends — flush as "completed".
                episodes.push(this.buildEpisode(cfg, current, "completed", null));
                current = null;
            }
        }

        // If the quote is still on this detour, finalize the open episode.
        if (isCurrentlyInside) {
            if (current) {
                episodes.push(this.buildEpisode(cfg, current, "active", this.status()));
            } else if (fallbackSource != null) {
                // Edge case: in detour but no recorded transition (data race
                // or missing log) — synthesize one from the fallback source.
                const srcIdx = MAIN_PATH.findIndex(n => n.status === fallbackSource);
                if (srcIdx >= 0) {
                    episodes.push(this.buildEpisode(
                        cfg,
                        { sourceStatus: fallbackSource, sourceIdx: srcIdx, entryDate: null, progressDate: null, wasInProgress: false },
                        "active",
                        this.status(),
                    ));
                }
            }
        }

        // Dedupe by sourceIdx — most recent wins. (Two episodes of the same
        // kind from the same column shouldn't render as overlapping arches.)
        const seen = new Set<number>();
        const unique: DetourEpisode[] = [];
        for (const ep of [...episodes].reverse()) {
            if (seen.has(ep.sourceIdx)) continue;
            seen.add(ep.sourceIdx);
            unique.push(ep);
        }
        const ordered = unique.reverse();

        // If the quote has never taken this detour (no log episode, not
        // currently inside), surface a dimmed placeholder anchored at the
        // canonical source. For live quotes this reads as "still available";
        // for terminal quotes (Lost / Archived / …) it reads as "path that
        // existed but wasn't taken". Either way the arch shouldn't vanish.
        if (ordered.length === 0) {
            const srcIdx = MAIN_PATH.findIndex(n => n.status === cfg.canonicalSourceStatus);
            if (srcIdx >= 0) {
                ordered.push(this.buildEpisode(
                    cfg,
                    { sourceStatus: cfg.canonicalSourceStatus, sourceIdx: srcIdx, entryDate: null, progressDate: null, wasInProgress: false },
                    "future",
                    null,
                ));
            }
        }

        return ordered;
    }

    /** Build a finalized DetourEpisode with per-node state and dates. Row is
     *  initialised to 0; the real row is assigned later in `allEpisodes()`
     *  once we know which arches share a source column. */
    private buildEpisode(
        cfg: DetourKindConfig,
        w: { sourceStatus: QuoteStatus; sourceIdx: number; entryDate: Date | null; progressDate: Date | null; wasInProgress: boolean },
        state: "active" | "completed" | "future",
        currentSubStatus: QuoteStatus | null,
    ): DetourEpisode {
        const nodes: RenderedNode[] = cfg.path.map((n): RenderedNode => {
            let s: NodeState;
            if (state === "completed") {
                s = "completed";
            } else if (state === "future") {
                s = "future";
            } else if (n.status === currentSubStatus) {
                s = "current";
            } else if (n.status === cfg.entryStatus) {
                // active episode: entry is "completed" if we've moved past it
                s = w.wasInProgress ? "completed" : (currentSubStatus === cfg.entryStatus ? "current" : "future");
            } else {
                s = "future";
            }
            const date = state === "future"
                ? null
                : n.status === cfg.entryStatus
                    ? w.entryDate
                    : n.status === cfg.progressStatus
                        ? w.progressDate
                        : null;
            return { ...n, state: s, date };
        });

        // Active episodes get an `at` that points at the sub-status the
        // quote currently sits on. The template uses it to leave the right
        // leg of the arch un-illuminated until the quote actually reaches
        // the progress sub-status.
        const at: EpisodeFocus = state !== "active"
            ? null
            : currentSubStatus === cfg.progressStatus
                ? "progress"
                : currentSubStatus === cfg.entryStatus
                    ? "entry"
                    : w.wasInProgress
                        ? "progress"
                        : "entry";

        return { kind: cfg.kind, sourceStatus: w.sourceStatus, sourceIdx: w.sourceIdx, state, nodes, row: 0, at };
    }
}
